import { useEffect, useRef, type ReactNode } from 'react';

export type RingsState = 'searching' | 'found' | 'connected' | 'lost';

/**
 * The DeskTouch signal rings: the connection's state made visible, the same
 * four states the apps use. Searching sends rings out of the core, found
 * sparks and settles, connected holds the mark's three rings, lost greys out.
 * A canvas that follows the system theme, pauses in a hidden tab and draws a
 * single still for anyone who asked for reduced motion.
 */
export function Rings({
  state,
  size = 120,
  fill = false,
  className,
  children,
}: {
  state: RingsState;
  /** Width and height in pixels; ignored with `fill`. */
  size?: number;
  /** Covers its positioned parent, as a background. */
  fill?: boolean;
  className?: string;
  /** Drawn over the core, e.g. a check mark. */
  children?: ReactNode;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    return mount(canvas, state);
  }, [state]);
  return (
    <span
      className={['rings', fill ? 'fill' : '', className ?? ''].join(' ').trim()}
      style={fill ? undefined : { width: size, height: size }}
      aria-hidden="true"
    >
      <canvas ref={ref} />
      {children && <span className="rings-glyph">{children}</span>}
    </span>
  );
}

type Rgb = [number, number, number];
const STOPS: Array<[number, Rgb]> = [
  [0, [47, 211, 245]],
  [0.32, [58, 123, 247]],
  [0.62, [138, 76, 245]],
  [0.86, [227, 63, 208]],
  [1, [242, 91, 176]],
];
const CYAN: Rgb = [47, 211, 245];
const AZURE: Rgb = [58, 123, 247];
const VIOLET: Rgb = [138, 76, 245];
const MINT: Rgb = [62, 227, 160];
const GREY: Rgb = [128, 136, 156];

const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));
const lerp = (a: number, b: number, k: number) => a + (b - a) * k;
const rgba = (c: Rgb, a: number) => `rgba(${c[0] | 0},${c[1] | 0},${c[2] | 0},${clamp(a, 0, 1).toFixed(3)})`;

function brand(u: number): Rgb {
  u = clamp(u, 0, 1);
  for (let i = 1; i < STOPS.length; i++) {
    if (u <= STOPS[i][0]) {
      const [u0, a] = STOPS[i - 1];
      const [u1, b] = STOPS[i];
      const k = (u - u0) / (u1 - u0);
      return [lerp(a[0], b[0], k), lerp(a[1], b[1], k), lerp(a[2], b[2], k)];
    }
  }
  return STOPS[STOPS.length - 1][1];
}

function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, c: Rgb, a: number) {
  if (r <= 0 || a <= 0) return;
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, rgba(c, a));
  g.addColorStop(1, rgba(c, 0));
  ctx.fillStyle = g;
  ctx.fillRect(x - r, y - r, 2 * r, 2 * r);
}

interface Ring {
  r: number;
  age: number;
  a: number;
  v: number;
  max: number;
}

interface Scene {
  w: number;
  h: number;
  u: number;
  cx: number;
  cy: number;
  dark: boolean;
  t: number;
  emit: number;
  rings: Ring[];
  /** How far the scene has settled into the mark: 0 searching, 1 connected. */
  settle: number;
}

function mount(canvas: HTMLCanvasElement, state: RingsState): () => void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => undefined;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const darkMedia = window.matchMedia('(prefers-color-scheme: dark)');
  let scene: Scene | null = null;
  let raf = 0;
  let last = 0;
  let dead = false;

  const build = () => {
    const rect = canvas.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const base = Math.min(rect.width, rect.height);
    scene = {
      w: rect.width,
      h: rect.height,
      // The core is drawn for a 120px box; a background scales it up a little, never past the box.
      u: clamp(base / 120, 0.5, 1.6),
      cx: rect.width / 2,
      cy: rect.height / 2,
      dark: darkMedia.matches,
      t: 0,
      emit: 0.3,
      rings: [],
      settle: state === 'connected' || state === 'lost' ? 1 : 0,
    };
    // Start mid-motion rather than from an empty field.
    for (let i = 0; i < 150; i++) step(scene, 1 / 60);
    draw(ctx, scene, state);
  };

  const step = (s: Scene, dt: number) => {
    s.t += dt;
    const searching = state === 'searching' || (state === 'found' && s.t < 1.1);
    if (state === 'found') s.settle = clamp((s.t - 1.1) / 1.2, 0, 1);
    if (searching) {
      s.emit -= dt;
      if (s.emit <= 0) {
        s.emit += state === 'found' ? 0.35 : 1.35;
        s.rings.push({ r: 34 * s.u, age: 0, a: 1, v: 120, max: Math.hypot(s.w, s.h) * 0.62 });
      }
    }
    for (const r of s.rings) {
      r.age += dt;
      r.r += r.v * s.u * dt * (1 - 0.45 * (r.r / r.max));
    }
    s.rings = s.rings.filter((r) => r.r < r.max);
  };

  const frame = (now: number) => {
    raf = 0;
    if (dead || !scene) return;
    const dt = Math.min(0.05, (now - (last || now)) / 1000);
    last = now;
    step(scene, dt);
    draw(ctx, scene, state);
    if (!reduced.matches && !document.hidden) raf = requestAnimationFrame(frame);
  };
  const play = () => {
    if (!raf && scene && !reduced.matches && !document.hidden) {
      last = 0;
      raf = requestAnimationFrame(frame);
    }
  };

  const observer = new ResizeObserver(() => {
    build();
    play();
  });
  observer.observe(canvas);
  const rebuild = () => {
    build();
    play();
  };
  darkMedia.addEventListener('change', rebuild);
  reduced.addEventListener('change', rebuild);
  document.addEventListener('visibilitychange', play);
  build();
  play();

  return () => {
    dead = true;
    cancelAnimationFrame(raf);
    observer.disconnect();
    darkMedia.removeEventListener('change', rebuild);
    reduced.removeEventListener('change', rebuild);
    document.removeEventListener('visibilitychange', play);
  };
}

function draw(ctx: CanvasRenderingContext2D, s: Scene, state: RingsState) {
  const { u, cx, cy } = s;
  const lost = state === 'lost';
  ctx.globalCompositeOperation = 'source-over';
  ctx.clearRect(0, 0, s.w, s.h);

  // the travelling rings
  ctx.globalCompositeOperation = s.dark ? 'lighter' : 'source-over';
  for (const r of s.rings) {
    const a = r.a * Math.pow(1 - r.r / r.max, 1.5) * Math.min(1, r.age * 5) * (1 - s.settle);
    if (a <= 0.003) continue;
    const g = ctx.createConicGradient(s.t * 0.25, cx, cy);
    for (let i = 0; i <= 8; i++) g.addColorStop(i / 8, rgba(brand(Math.abs(1 - (i / 8) * 2) * 0.9 + 0.05), a * 0.9));
    ctx.beginPath();
    ctx.arc(cx, cy, r.r, 0, 7);
    ctx.strokeStyle = g;
    ctx.lineWidth = (1.1 + 1.6 * (1 - r.r / r.max)) * u;
    ctx.stroke();
  }
  ctx.globalCompositeOperation = 'source-over';

  // the core: a glow, the mark's three rings, the orb
  const breathe = state === 'connected' ? 1 + 0.025 * Math.sin(s.t * 1.6) : 1;
  const k = u * breathe;
  if (!lost) {
    glow(ctx, cx, cy, 110 * k, CYAN, s.dark ? 0.3 : 0.2);
    glow(ctx, cx, cy, 60 * k, VIOLET, s.dark ? 0.35 : 0.16);
    if (state === 'found' || state === 'connected') glow(ctx, cx, cy, 44 * k, MINT, 0.25 * s.settle);
  }
  const ringColour: Rgb = lost ? GREY : s.dark ? [255, 255, 255] : [18, 22, 32];
  const rings: Array<[number, number, number]> = [
    [34, 1.6, 0.35],
    [28, 2, 0.5],
    [21.6, 2.4, 0.75],
  ];
  rings.forEach(([r, wd, a], i) => {
    // Searching, the outer rings open up and fade a little with each pulse; settled, they hold still.
    const open = (1 - s.settle) * (0.5 + 0.5 * Math.sin(s.t * 4.6 - i)) * 3 * u;
    ctx.beginPath();
    ctx.arc(cx, cy, r * k + open, 0, 7);
    ctx.strokeStyle = rgba(ringColour, a * (s.dark || lost ? 0.9 : 0.6));
    ctx.lineWidth = wd * k;
    ctx.stroke();
  });
  const g = ctx.createLinearGradient(cx - 14 * k, cy - 14 * k, cx + 14 * k, cy + 14 * k);
  if (lost) {
    g.addColorStop(0, rgba(GREY, 1));
    g.addColorStop(1, rgba([90, 96, 112], 1));
  } else {
    g.addColorStop(0, rgba(CYAN, 1));
    g.addColorStop(0.55, rgba(AZURE, 1));
    g.addColorStop(1, rgba(VIOLET, 1));
  }
  ctx.beginPath();
  ctx.arc(cx, cy, 16 * k, 0, 7);
  ctx.fillStyle = g;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx - 4.8 * k, cy - 5.6 * k, 5 * k, 0, 7);
  ctx.fillStyle = 'rgba(255,255,255,.35)';
  ctx.fill();
}
